import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, TrendingUp, ChevronRight, ChevronDown, RefreshCw, Star, Zap } from 'lucide-react';
import ArbitrageCardMini from './ArbitrageCardMini';
import EVCardMini from './EVCardMini';
import ArbitrageCard from './ArbitrageCard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';



const LEAGUE_LOGO_URLS = {
  basketball_nba:            'https://a.espncdn.com/i/teamlogos/leagues/500/nba.png',
  americanfootball_nfl:      'https://a.espncdn.com/i/teamlogos/leagues/500/nfl.png',
  icehockey_nhl:             'https://a.espncdn.com/i/teamlogos/leagues/500/nhl.png',
  baseball_mlb:              'https://a.espncdn.com/i/teamlogos/leagues/500/mlb.png',
  basketball_wnba:           'https://a.espncdn.com/i/teamlogos/leagues/500/wnba.png',
};

const LEAGUE_EMOJI_FALLBACK = {
  basketball_ncaab:       '🏀',
  americanfootball_ncaaf: '🏈',
  basketball_ncaaw:       '🏀',
  basketball_euroleague:  '🏀',
  mma_mixed_martial_arts: '🥊',
  boxing_boxing:          '🥊',
  soccer_:                '⚽',
  tennis_:                '🎾',
};

function LeagueLogo({ sportKey, size = 16 }) {
  const url = LEAGUE_LOGO_URLS[sportKey];
  const emoji = LEAGUE_EMOJI_FALLBACK[sportKey] || (sportKey?.startsWith('soccer_') ? '⚽' : sportKey?.startsWith('tennis_') ? '🎾' : null);
  if (url) return <img src={url} alt={sportKey} style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />;
  if (emoji) return <span style={{ fontSize: size * 0.85, lineHeight: 1 }}>{emoji}</span>;
  return null;
}

const EV_SPORT_OPTIONS = [
  { label: 'NBA',         sportKey: 'basketball_nba',         match: o => o.sport_key === 'basketball_nba' },
  { label: 'NFL',         sportKey: 'americanfootball_nfl',   match: o => o.sport_key === 'americanfootball_nfl' },
  { label: 'NHL',         sportKey: 'icehockey_nhl',          match: o => o.sport_key === 'icehockey_nhl' },
  { label: 'MLB',         sportKey: 'baseball_mlb',           match: o => o.sport_key === 'baseball_mlb' },
  { label: 'NCAAB',       sportKey: 'basketball_ncaab',       match: o => o.sport_key === 'basketball_ncaab' },
  { label: 'NCAAF',       sportKey: 'americanfootball_ncaaf', match: o => o.sport_key === 'americanfootball_ncaaf' },
  { label: 'WNBA',        sportKey: 'basketball_wnba',        match: o => o.sport_key === 'basketball_wnba' },
  { label: 'NCAAW',       sportKey: 'basketball_ncaaw',       match: o => o.sport_key === 'basketball_ncaaw' },
  { label: 'EuroLeague',  sportKey: 'basketball_euroleague',  match: o => o.sport_key === 'basketball_euroleague' },
  { label: 'UFC',         sportKey: 'mma_mixed_martial_arts', match: o => o.sport_key === 'mma_mixed_martial_arts' },
  { label: 'Soccer',      sportKey: 'soccer_',                match: o => o.sport_key.startsWith('soccer_') },
  { label: 'Tennis',      sportKey: 'tennis_',                match: o => o.sport_key.startsWith('tennis_') },
  { label: 'Boxing',      sportKey: 'boxing_boxing',          match: o => o.sport_key === 'boxing_boxing' },
];

function OpportunityDashboard({ selectedBooks, timeWindow, liveStatus, maxOdds, bankroll = 1000, kellyFraction = 0.25, onViewGame, onNavigateToSport, showOnlyEV = false, showOnlyArbitrage = false, session }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArb, setSelectedArb] = useState(null);
  const [expandedArb, setExpandedArb] = useState(false);
  const [expandedEV, setExpandedEV] = useState(false);
  const [expandedFavorites, setExpandedFavorites] = useState(false);
  const [expandedUnderdogs, setExpandedUnderdogs] = useState(false);
  const [evSportIdx, setEvSportIdx] = useState('all');
  const [arbSportIdx, setArbSportIdx] = useState('all');
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const fetchOpportunities = async () => {
    setLoading(true);
    setError(null);

    try {
      const maxArb = (showOnlyArbitrage || expandedArb) ? 50 : 6;
      const maxEV = (expandedEV || expandedFavorites || expandedUnderdogs) ? 50 : 12;
      let url = `${API_BASE_URL}/opportunities?max_arb=${maxArb}&max_ev=${maxEV}`;
      if (selectedBooks && selectedBooks.length > 0) {
        url += `&books=${selectedBooks.join(',')}`;
      }
      if (timeWindow) {
        url += `&time_window=${timeWindow}`;
      }
      if (liveStatus && liveStatus !== 'all') {
        url += `&live_status=${liveStatus}`;
      }
      if (maxOdds !== null && maxOdds !== undefined) {
        url += `&max_odds=${maxOdds}`;
      }

      const response = await fetch(url);
      const result = await response.json();

      if (result.success) {
        if (result.loading) {
          // Backend cache is still warming up — retry in 3 seconds
          setTimeout(fetchOpportunities, 3000);
          return;
        }
        setData(result);
        setLastFetchedAt(Date.now());
      } else {
        setError('Failed to fetch opportunities');
      }
    } catch (err) {
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  // Always keep a ref to the latest fetchOpportunities so timers never use stale closures
  const fetchRef = useRef(null);
  fetchRef.current = fetchOpportunities;

  // Re-fetch immediately when any filter param changes
  useEffect(() => {
    fetchRef.current();
  }, [selectedBooks, timeWindow, liveStatus, maxOdds, expandedArb, expandedEV, expandedFavorites, expandedUnderdogs]);

  // Auto-refresh: schedule next fetch 60s after the last successful fetch completes.
  // Using lastFetchedAt (local time) instead of data.timestamp so the timer always
  // resets even when the backend returns a cached response with the same timestamp.
  useEffect(() => {
    if (!lastFetchedAt) return;
    const timer = setTimeout(() => fetchRef.current?.(), 60000);
    return () => clearTimeout(timer);
  }, [lastFetchedAt]);



  const handleCalculate = (opp) => {
    setSelectedArb(opp);
  };

  if (loading && !data) {
    return (
      <div className="opportunity-dashboard loading-state">
        <RefreshCw className="spinning" size={24} />
        <p>Loading opportunities...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="opportunity-dashboard error-state">
        <AlertCircle size={24} />
        <p>{error}</p>
        <button onClick={fetchOpportunities}>Retry</button>
      </div>
    );
  }

  const showArbitrage = !showOnlyEV;
  const showEV = !showOnlyArbitrage;

  // Combine game odds EV with prop EV (always include props), then sort by EV %
  const gameEVOpps = data?.ev_opportunities || [];
  const propEVOpps = data?.prop_ev_opportunities || [];
  const allEVOpps = [...gameEVOpps, ...propEVOpps]
    .sort((a, b) => b.ev_percentage - a.ev_percentage);

  // Apply sport filter using fixed sport groups
  const visibleEVOpps = evSportIdx === 'all'
    ? allEVOpps
    : allEVOpps.filter(EV_SPORT_OPTIONS[evSportIdx].match);

  // Split into favorites / underdogs (props now included in both based on odds)
  // Note: Odds > +200 are excluded from display
  const favorites = visibleEVOpps.filter(o => o.odds < 0);
  const underdogs = visibleEVOpps.filter(o => o.odds >= 100 && o.odds <= 200);

  // Combine game arb with prop arb (always include props), then sort by profit %
  const gameArbOpps = (data?.arbitrage_opportunities || []).filter(opp => {
    if (!selectedBooks || selectedBooks.length === 0) return true;
    // For game arb, check both sides are in selected books
    if (opp.market !== 'player_props') {
      return selectedBooks.includes(opp.home_sportsbook_key) &&
             selectedBooks.includes(opp.away_sportsbook_key);
    }
    return true;
  });
  const propArbOpps = (data?.prop_arb_opportunities || []).filter(opp => {
    if (!selectedBooks || selectedBooks.length === 0) return true;
    // For prop arb, check both over/under books are in selected books
    return selectedBooks.includes(opp.over_sportsbook_key) &&
           selectedBooks.includes(opp.under_sportsbook_key);
  });
  // Combine and sort all arb opportunities by profit percentage (highest first)
  const allArbOpps = [...gameArbOpps, ...propArbOpps]
    .sort((a, b) => b.profit_percentage - a.profit_percentage);

  const visibleArbOpps = arbSportIdx === 'all'
    ? allArbOpps
    : allArbOpps.filter(EV_SPORT_OPTIONS[arbSportIdx].match);

  const hasArbitrageOpps = allArbOpps.length > 0;

  return (
    <div className="opportunity-dashboard">
      {/* Arbitrage Section */}
      {showArbitrage && (
        <section className="dashboard-section arb-section">
          <div className="section-header">
            <div className="section-title">
              <AlertCircle className="section-icon arb-icon" size={20} />
              <h2>Top Arbitrage Opportunities</h2>
              {allArbOpps.length > 0 && (
                <span className="opp-count">{allArbOpps.length}</span>
              )}
            </div>
            {hasArbitrageOpps && (
              <button className="view-all-btn" onClick={() => setExpandedArb(!expandedArb)}>
                {expandedArb ? 'Show Less' : 'View All'} {expandedArb ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            )}
          </div>


          {/* Arb sport filter pills — dedicated arb page only */}
          {showOnlyArbitrage && (
            <div className="ev-filter-row">
              <div className="ev-sport-filter">
                <button
                  className={`ev-sport-pill${arbSportIdx === 'all' ? ' active' : ''}`}
                  onClick={() => setArbSportIdx('all')}
                >All</button>
                {EV_SPORT_OPTIONS.map((opt, idx) => {
                  const count = allArbOpps.filter(opt.match).length;
                  return (
                    <button
                      key={idx}
                      className={`ev-sport-pill${arbSportIdx === idx ? ' active' : ''}${count === 0 ? ' empty' : ''}`}
                      onClick={() => setArbSportIdx(idx)}
                    >
                      <LeagueLogo sportKey={opt.sportKey} size={15} />
                      {opt.label}
                      {count > 0 && <span className="ev-sport-count">{count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {hasArbitrageOpps ? (
            <div className={`dashboard-cards arb-cards ${expandedArb ? 'expanded' : ''}`}>
              {visibleArbOpps.map((opp, idx) => (
                <ArbitrageCardMini
                  key={idx}
                  opportunity={opp}
                  onViewGame={onViewGame}
                  onCalculate={handleCalculate}
                  session={session}
                />
              ))}
            </div>
          ) : (
            <div className="empty-section">
              <p>No arbitrage opportunities found right now</p>
              <span className="empty-hint">Check back soon - opportunities appear as odds change</span>
            </div>
          )}
        </section>
      )}

      {/* EV Section — combined view (home page / arbitrage page) */}
      {showEV && !showOnlyEV && (
        <section className="dashboard-section ev-section">
          <div className="section-header">
            <div className="section-title">
              <TrendingUp className="section-icon ev-icon" size={20} />
              <h2>Top +EV Bets</h2>
              {data?.ev_count > 0 && (
                <span className="opp-count">{data.ev_count}</span>
              )}
            </div>
            {allEVOpps.length > 0 && (
              <button className="view-all-btn" onClick={() => setExpandedEV(!expandedEV)}>
                {expandedEV ? 'Show Less' : 'View All'} {expandedEV ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            )}
          </div>

          {allEVOpps.length > 0 ? (
            <div className={`dashboard-cards ev-cards ${expandedEV ? 'expanded' : ''}`}>
              {allEVOpps.map((opp, idx) => (
                <EVCardMini
                  key={idx}
                  opportunity={opp}
                  bankroll={bankroll}
                  kellyFraction={kellyFraction}
                  onViewGame={onViewGame}
                  session={session}
                />
              ))}
            </div>
          ) : (
            <div className="empty-section">
              <p>No +EV opportunities found right now</p>
              <span className="empty-hint">Opportunities appear when sportsbook odds exceed fair value</span>
            </div>
          )}
        </section>
      )}

      {/* EV Section — split Favorites / Underdogs / Props (EV+ dedicated page) */}
      {showEV && showOnlyEV && (
        <section className="dashboard-section ev-section">
          <>
              {/* Filter row: Sport pills */}
              <div className="ev-filter-row">
                <div className="ev-sport-filter">
                  <button
                    className={`ev-sport-pill${evSportIdx === 'all' ? ' active' : ''}`}
                    onClick={() => setEvSportIdx('all')}
                  >All</button>
              {EV_SPORT_OPTIONS.map((opt, idx) => {
                // Only count opportunities that will actually be displayed (favorites or underdogs +100 to +200)
                const count = allEVOpps.filter(o => opt.match(o) && (o.odds < 0 || (o.odds >= 100 && o.odds <= 200))).length;
                return (
                  <button
                    key={idx}
                    className={`ev-sport-pill${evSportIdx === idx ? ' active' : ''}${count === 0 ? ' empty' : ''}`}
                    onClick={() => setEvSportIdx(idx)}
                  >
                    <LeagueLogo sportKey={opt.sportKey} size={15} />
                    {opt.label}
                    {count > 0 && <span className="ev-sport-count">{count}</span>}
                  </button>
                );
              })}
                </div>
              </div>

              {/* Favorites sub-section */}
              <div className="ev-subsection">
                <div className="ev-subsection-header">
                  <div className="ev-subsection-title">
                    <Star size={15} />
                    Favorites
                    <span className="ev-subsection-badge">{favorites.length}</span>
                  </div>
                  {favorites.length > 6 && (
                    <button className="view-all-btn" onClick={() => setExpandedFavorites(!expandedFavorites)}>
                      {expandedFavorites ? 'Show Less' : 'View All'} {expandedFavorites ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  )}
                </div>
                {favorites.length > 0 ? (
                  <div className={`dashboard-cards ev-cards ${expandedFavorites ? 'expanded' : ''}`}>
                    {(expandedFavorites ? favorites : favorites.slice(0, 6)).map((opp, idx) => (
                      <EVCardMini
                        key={idx}
                        opportunity={opp}
                        bankroll={bankroll}
                        kellyFraction={kellyFraction}
                        onViewGame={onViewGame}
                        session={session}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="empty-section">
                    <p>No +EV favorites found right now</p>
                  </div>
                )}
              </div>

              {/* Underdogs sub-section */}
              <div className="ev-subsection">
                <div className="ev-subsection-header">
                  <div className="ev-subsection-title">
                    <Zap size={15} />
                    Underdogs
                    <span className="ev-subsection-badge">{underdogs.length}</span>
                  </div>
                  {underdogs.length > 6 && (
                    <button className="view-all-btn" onClick={() => setExpandedUnderdogs(!expandedUnderdogs)}>
                      {expandedUnderdogs ? 'Show Less' : 'View All'} {expandedUnderdogs ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  )}
                </div>
                {underdogs.length > 0 ? (
                  <div className={`dashboard-cards ev-cards ${expandedUnderdogs ? 'expanded' : ''}`}>
                    {(expandedUnderdogs ? underdogs : underdogs.slice(0, 6)).map((opp, idx) => (
                      <EVCardMini
                        key={idx}
                        opportunity={opp}
                        bankroll={bankroll}
                        kellyFraction={kellyFraction}
                        onViewGame={onViewGame}
                        session={session}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="empty-section">
                    <p>No +EV underdogs found right now</p>
                  </div>
                )}
              </div>
            </>
        </section>
      )}

      {/* Calculator Modal */}
      {selectedArb && (
        <div className="calc-modal-overlay" onClick={() => setSelectedArb(null)}>
          <div className="calc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setSelectedArb(null)}>×</button>
            <ArbitrageCard opportunity={selectedArb} onViewGame={onViewGame} session={session} />
          </div>
        </div>
      )}
    </div>
  );
}

export default OpportunityDashboard;
